import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'components/ui/button';
import ManagedCheckbox from 'components/form/managed_input/checkbox';
import ManagedInput from 'components/form/managed_input';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import Options from 'components/form/managed_input/options';
import {
  BUY_SIDE,
  getTransactionSideLabel,
  SELL_SIDE
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';
import { commissionModes, FLAT_RATE, PERCENT_OF_RENT, RATE_PER_SPACE_SIZE_UNIT } from './commission_modes';
import { leaseRateUnitOptions } from 'components/lease_structure/form/helpers/select_options';

const defaultState = {
  buySideCommissionPercent: 0,
  buySideCommissionRate: 0,
  commissionPercent: 0,
  commissionRate: 0,
  duration: null,
  durationUnits: 'years',
  escalationFrequency: 0,
  escalationFrequencyUnits: 'years',
  escalationRate: 0,
  escalationRateUnits: 'percent',
  freeRent: 0,
  freeRentUnits: 'months',
  initialLeaseRate: null,
  leaseRateUnits: 'per_space_size_unit_per_year',
  operatingExpensesRate: 0,
  useFreeRent: false,
  sellSideCommissionPercent: 0,
  sellSideCommissionRate: 0,
  startDate: null,
  useTransactionSideValues: false,
};

class RentEscalationModal extends React.Component {
  static propTypes = {
    buySideCommissionPercent: PropTypes.number,
    buySideCommissionRate: PropTypes.number,
    commissionMode: PropTypes.oneOf(commissionModes).isRequired,
    commissionPercent: PropTypes.number,
    commissionRate: PropTypes.number,
    duration: PropTypes.number,
    durationUnits: PropTypes.string,
    enableOperatingExpenses: PropTypes.bool,
    headerLabel: PropTypes.string,
    initialLeaseRate: PropTypes.number,
    leaseRateUnits: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    sellSideCommissionPercent: PropTypes.number,
    sellSideCommissionRate: PropTypes.number,
    show: PropTypes.bool,
    spaceSizeUnits: PropTypes.string,
    transactionSideOptions: PropTypes.array,
    useTransactionSideValues: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
  }

  static defaultProps = {
    headerLabel: 'Rent Escalation',
    saveButtonLabel: 'Add Lease Terms',
    transactionSideOptions: [],
  }

  state = { ...defaultState };

  componentDidUpdate(previousProps) {
    const readProp = key => this.props[key] == null ? defaultState[key] : this.props[key];
    const useTransactionSideValues = this.props.useTransactionSideValues;

    if (!previousProps.show && this.props.show) {
      this.setState({
        buySideCommissionPercent: useTransactionSideValues ? readProp('buySideCommissionPercent') : 0,
        buySideCommissionRate: useTransactionSideValues ? readProp('buySideCommissionRate') : 0,
        commissionPercent: useTransactionSideValues ? 0 : readProp('commissionPercent'),
        commissionRate: useTransactionSideValues ? 0 : readProp('commissionRate'),
        duration: readProp('duration'),
        durationUnits: readProp('durationUnits'),
        escalationFrequency: 0,
        escalationFrequencyUnits: 'years',
        escalationRate: 0,
        escalationRateUnits: 'percent',
        freeRent: 0,
        freeRentUnits: 'months',
        initialLeaseRate: readProp('initialLeaseRate'),
        operatingExpensesRate: readProp('operatingExpensesRate'),
        leaseRateUnits: readProp('leaseRateUnits'),
        useFreeRent: false,
        sellSideCommissionPercent: useTransactionSideValues ? readProp('sellSideCommissionPercent') : 0,
        sellSideCommissionRate: useTransactionSideValues ? readProp('sellSideCommissionRate') : 0,
        useTransactionSideValues: readProp('useTransactionSideValues'),
        startDate: null
      });
    }
  }

  handleChange = (changes) => {
    this.setState(changes);
  }

  handleSelectChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  handleSubmit = () => {
    const escalation = {
      ...this.state,
      includeOperatingExpenses: this.state.operatingExpensesRate > 0,
    };

    this.props.onSubmit(escalation);
    this.props.onClose();
  }

  render() {
    return (
      <Modal
        backdrop="static"
        show={this.props.show}
        onCancel={this.props.onClose}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          {this.props.headerLabel}
        </Modal.Header>

        <Modal.Body>
          {this.renderForm()}
        </Modal.Body>

        <Modal.Footer>
          {this.renderControls()}
        </Modal.Footer>
      </Modal>
    );
  }

  renderControls() {
    return <React.Fragment>
      <Button label="Cancel" variant="ghost" onClick={this.props.onClose} />
      <Button
        disabled={
          this.isMissingRequiredFields() ||
          this.isFreeRentInvalid()
        }
        label={this.props.saveButtonLabel}
        onClick={this.handleSubmit}
      />
    </React.Fragment>;
  }

  // Only the term fields and the initial rate are required, and they must be positive.
  // Escalation and free rent are optional, so a value of 0 (no escalation / no free rent)
  // must be treated as valid input for those.
  isMissingRequiredFields() {
    const { startDate, duration, initialLeaseRate } = this.state;
    return startDate == null || !(duration > 0) || !(initialLeaseRate > 0);
  }

  isFreeRentInvalid() {
    if (!this.state.useFreeRent) return false;

    const { freeRent, freeRentUnits, duration, durationUnits } = this.state;
    if (freeRent == null || freeRent < 0) return true;

    const freeRentMonths = freeRentUnits === 'years' ? freeRent * 12 : freeRent;
    const durationMonths = durationUnits === 'years' ? duration * 12 : duration;
    return freeRentMonths > durationMonths;
  }

  renderForm() {
    return (
      <div className="form-vertical-field-container">
        <h6>Term</h6>

        <div className="form-group-pair">
          <ManagedFormGroup
            aria-label="start-date"
            label="Start Date"
            name="startDate"
            type="date"
            value={this.state.startDate}
            onChange={this.handleChange}
          />

          <ManagedFormGroup
            aria-label="lease-term"
            label="Lease Term"
            name="duration"
            type="number"
            value={this.state.duration}
            onChange={this.handleChange}
          />

          <ManagedInput
            className="form-control"
            name="durationUnits"
            type="select"
            value={this.state.durationUnits}
            onChange={this.handleChange}
          >
            <Options
              options={[
                { label: 'Years', value: 'years' },
                { label: 'Months', value: 'months' },
              ]}
            />
          </ManagedInput>
        </div>

        <ManagedCheckbox
          aria-label="free-rent"
          checked={this.state.useFreeRent}
          label="Free Rent"
          name="useFreeRent"
          wrapperClassName="form-label"
          onChange={this.handleChange}
        />

        <div className="form-group-pair">
          <ManagedFormGroup
            aria-label="free-rent-amount"
            disabled={!this.state.useFreeRent}
            label={null}
            name="freeRent"
            type="number"
            value={this.state.freeRent}
            onChange={change => this.handleChange({
              // If freeRent is null, assign it to 0 to prevent the submit button from
              // being disabled
              ...change,
              ...(!change.freeRent && { freeRent: 0 }),
            })}
          />

          <ManagedInput
            className="form-control"
            disabled={!this.state.useFreeRent}
            name="freeRentUnits"
            type="select"
            value={this.state.freeRentUnits}
            onChange={this.handleChange}
          >
            <Options
              options={[
                { label: 'Years', value: 'years' },
                { label: 'Months', value: 'months' },
              ]}
            />
          </ManagedInput>
        </div>

        {this.props.enableOperatingExpenses &&
          <>
            <h6>Operating Expenses</h6>
            <ManagedFormGroup
              label="Operating Expenses Rate"
              name="operatingExpensesRate"
              type="currency"
              value={this.state.operatingExpensesRate}
              onChange={this.handleChange}
            />
          </>
        }

        <h6>Escalation</h6>

        <div className="form-group-pair">
          <ManagedFormGroup
            aria-label="initial-lease-rate"
            label={`Initial ${this.props.enableOperatingExpenses ? 'Base ' : ''}Lease Rate`}
            name="initialLeaseRate"
            type="currency"
            value={this.state.initialLeaseRate}
            onChange={this.handleChange}
          />

          <ManagedInput
            className="form-control"
            name="leaseRateUnits"
            type="select"
            value={this.state.leaseRateUnits}
            onChange={this.handleChange}
          >
            <Options options={leaseRateUnitOptions({ spaceSizeUnits: this.props.spaceSizeUnits || 'sf' })} />
          </ManagedInput>
        </div>

        <div className="form-group-pair">
          <ManagedFormGroup
            aria-label="escalation-amount"
            label="Escalation Amount"
            name="escalationRate"
            type={this.state.escalationRateUnits === 'percent' ? 'number' : 'currency'}
            value={this.state.escalationRate}
            onChange={this.handleChange}
          />

          <ManagedInput
            aria-label="escalation-rate-units"
            className="form-control rent-escalation-select"
            name="escalationRateUnits"
            type="select"
            value={this.state.escalationRateUnits}
            onChange={this.handleChange}
          >
            <Options
              options={[
                { label: 'Percent', value: 'percent' },
                { label: 'Dollars', value: 'dollars' },
              ]}
            />
          </ManagedInput>
        </div>

        <div className="form-group-pair">
          <ManagedFormGroup
            aria-label="escalation-frequency"
            label="Escalate Every"
            name="escalationFrequency"
            type="number"
            value={this.state.escalationFrequency}
            onChange={this.handleChange}
          />

          <ManagedInput
            className="form-control rent-escalation-select"
            name="escalationFrequencyUnits"
            type="select"
            value={this.state.escalationFrequencyUnits}
            onChange={this.handleChange}
          >
            <Options
              options={[
                { label: 'Years', value: 'years' },
                { label: 'Months', value: 'months' },
              ]}
            />
          </ManagedInput>
        </div>

        {this.state.useTransactionSideValues
          ? this.renderTransactionSideCommissionAmounts()
          : this.renderCommissionAmount()}
      </div>
    );
  }

  renderCommissionAmount() {
    if ([RATE_PER_SPACE_SIZE_UNIT, FLAT_RATE].includes(this.props.commissionMode)) {
      return;
    }

    const isPercent = this.props.commissionMode === PERCENT_OF_RENT;
    return (
      <ManagedFormGroup
        aria-label={isPercent ? 'commission-percent' : 'commission-rate'}
        label={`Commission ${isPercent ? 'Percent' : 'Rate'}`}
        name={isPercent ? 'commissionPercent' : 'commissionRate'}
        type={isPercent ? 'number' : 'currency'}
        value={isPercent ? this.state.commissionPercent : this.state.commissionRate}
        onChange={this.handleChange}
      />
    );
  }

  renderTransactionSideCommissionAmounts() {
    if ([RATE_PER_SPACE_SIZE_UNIT, FLAT_RATE].includes(this.props.commissionMode)) {
      return;
    }

    const isPercent = this.props.commissionMode === PERCENT_OF_RENT;
    const sellSideLabel = getTransactionSideLabel(SELL_SIDE, this.props.transactionSideOptions);
    const buySideLabel = getTransactionSideLabel(BUY_SIDE, this.props.transactionSideOptions);
    return (
      <>
        <ManagedFormGroup
          aria-label={isPercent ? 'sell_side-commission-percent' : 'sell_side-commission-rate'}
          label={`${sellSideLabel} Commission ${isPercent ? '%' : 'Rate'}`}
          name={isPercent ? 'sellSideCommissionPercent' : 'sellSideCommissionRate'}
          type={isPercent ? 'number' : 'currency'}
          value={isPercent ? this.state.sellSideCommissionPercent : this.state.sellSideCommissionRate}
          onChange={this.handleChange}
        />

        <ManagedFormGroup
          aria-label={isPercent ? 'buy_side-commission-percent' : 'buy_side-commission-rate'}
          label={`${buySideLabel} Commission ${isPercent ? '%' : 'Rate'}`}
          name={isPercent ? 'buySideCommissionPercent' : 'buySideCommissionRate'}
          type={isPercent ? 'number' : 'currency'}
          value={isPercent ? this.state.buySideCommissionPercent : this.state.buySideCommissionRate}
          onChange={this.handleChange}
        />
      </>
    );
  }
}

export default RentEscalationModal;
